import { z } from 'zod';
import { request } from '@/lib/api';

export const PO_KEY = ['purchase-orders'] as const;
export const BILLS_KEY = ['supplier-invoices'] as const;

export const poStatus = z.enum(['DRAFT', 'ORDERED', 'RECEIVED', 'CANCELLED']);
export type PoStatus = z.infer<typeof poStatus>;

export const purchaseOrderSchema = z.object({
  id: z.string().uuid(),
  number: z.number(),
  status: poStatus,
  expectedAt: z.string().nullable(),
  orderedAt: z.string().nullable(),
  receivedAt: z.string().nullable(),
  notes: z.string().nullable(),
  total: z.string(),
  taskId: z.string().nullable(),
  createdAt: z.string(),
  supplier: z.object({ id: z.string(), name: z.string(), phone: z.string().nullish(), email: z.string().nullish() }),
  lines: z.array(
    z.object({
      id: z.string(),
      description: z.string(),
      sku: z.string().nullable(),
      qty: z.string(),
      unit: z.string().nullable(),
      unitPrice: z.string().nullable(),
      amount: z.string().nullable(),
    }),
  ),
  invoices: z.array(z.object({ id: z.string(), invoiceNumber: z.string().nullable(), amount: z.string().nullable(), status: z.string() })),
});
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

export interface PoLineInput {
  description: string;
  sku?: string;
  qty: number;
  unit?: string;
  unitPrice?: number | null;
}
export interface PoInput {
  supplierId: string;
  lines: PoLineInput[];
  expectedAt?: string | null;
  notes?: string | null;
  taskId?: string;
}

export const billStatus = z.enum(['RECEIVED', 'APPROVED', 'PAID', 'DISPUTED']);
export type BillStatus = z.infer<typeof billStatus>;

export const billSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string().nullable(),
  issueDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  amount: z.string().nullable(),
  vatAmount: z.string().nullable(),
  status: billStatus,
  approvedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  notes: z.string().nullable(),
  fileName: z.string().nullable(),
  hasFile: z.boolean(),
  /** הגיעה לבד מהמייל (Gmail או העברה) — לא הועלתה ידנית. */
  fromEmail: z.boolean().catch(false),
  sourceFrom: z.string().nullable().catch(null),
  extracted: z
    .object({ supplierName: z.string().nullable(), supplierTaxId: z.string().nullable(), confidence: z.number() })
    .partial()
    .nullable()
    .catch(null),
  createdAt: z.string(),
  supplierId: z.string().nullable(),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable(),
  purchaseOrderId: z.string().nullable(),
  purchaseOrder: z.object({ id: z.string(), number: z.number(), total: z.string() }).nullable(),
  contractId: z.string().nullable(),
  contract: z.object({ id: z.string(), title: z.string() }).nullable(),
  mismatch: z.enum(['higher', 'lower']).nullable(),
});
export type Bill = z.infer<typeof billSchema>;
const billResult = z.object({ invoice: billSchema, duplicateOf: z.object({ id: z.string(), status: z.string() }).nullable() });

export interface BillInput {
  supplierId?: string | null;
  invoiceNumber?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  amount?: number | null;
  vatAmount?: number | null;
  purchaseOrderId?: string | null;
  contractId?: string | null;
  notes?: string | null;
}

export const purchasingApi = {
  orders: (signal?: AbortSignal) => request('/purchase-orders', { schema: z.array(purchaseOrderSchema), signal }),
  createOrder: (v: PoInput) => request('/purchase-orders', { method: 'POST', body: v, schema: purchaseOrderSchema }),
  updateOrder: (id: string, v: PoInput) => request(`/purchase-orders/${id}`, { method: 'PATCH', body: v, schema: purchaseOrderSchema }),
  sendOrder: (id: string) =>
    request(`/purchase-orders/${id}/order`, {
      method: 'POST',
      schema: z.object({ order: purchaseOrderSchema, delivery: z.object({ channel: z.string(), status: z.string() }) }),
    }),
  receiveOrder: (id: string) =>
    request(`/purchase-orders/${id}/receive`, {
      method: 'POST',
      schema: z.object({ order: purchaseOrderSchema, restocked: z.array(z.object({ sku: z.string(), qty: z.number() })) }),
    }),
  cancelOrder: (id: string) => request(`/purchase-orders/${id}/cancel`, { method: 'POST', schema: purchaseOrderSchema }),

  bills: (signal?: AbortSignal) => request('/supplier-invoices', { schema: z.array(billSchema), signal }),
  uploadBill: (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request('/supplier-invoices/upload', { method: 'POST', body, schema: billResult });
  },
  createBill: (v: BillInput) => request('/supplier-invoices', { method: 'POST', body: v, schema: billResult }),
  updateBill: (id: string, v: BillInput) => request(`/supplier-invoices/${id}`, { method: 'PATCH', body: v, schema: billResult }),
  approveBill: (id: string) => request(`/supplier-invoices/${id}/approve`, { method: 'POST', schema: billSchema }),
  payBill: (id: string, v: { paidAt?: string; paymentMethod?: string }) =>
    request(`/supplier-invoices/${id}/pay`, { method: 'POST', body: v, schema: billSchema }),
  disputeBill: (id: string, reason: string) =>
    request(`/supplier-invoices/${id}/dispute`, { method: 'POST', body: { reason }, schema: billSchema }),
};

// התווית: purchasing:poStatus.<סטטוס> — מתורגמת בתצוגה.
export const PO_STATUS: Record<PoStatus, { tone: 'neutral' | 'info' | 'success' | 'danger' }> = {
  DRAFT: { tone: 'neutral' },
  ORDERED: { tone: 'info' },
  RECEIVED: { tone: 'success' },
  CANCELLED: { tone: 'danger' },
};

// התווית: purchasing:billStatus.<סטטוס> — מתורגמת בתצוגה.
export const BILL_STATUS: Record<BillStatus, { tone: 'neutral' | 'info' | 'success' | 'warning' }> = {
  RECEIVED: { tone: 'warning' },
  APPROVED: { tone: 'info' },
  PAID: { tone: 'success' },
  DISPUTED: { tone: 'neutral' },
};

/**
 * אמצעי תשלום — מפתחות ל-purchasing:paymentMethods.*. נשמר בשרת כטקסט חופשי:
 * התווית בשפת הממשק (בעברית — בדיוק כמו קודם).
 */
export const PAYMENT_METHODS = ['bankTransfer', 'creditCard', 'check', 'cash', 'standingOrder', 'bit', 'other'] as const;
